import React, { useState } from 'react';

import { Alert, Box, Button, Group, Stack } from '@mantine/core';
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';

interface SetupPaymentMethodFormProps {
  athleteEmail: string;
  athleteFullName: string;
  athletePhone?: string | null;
  onSuccess: (confirmationTokenId: string) => void;
  onCancel: () => void;
  onError?: (error: string) => void;
  paymentMethodMetadata?: Record<string, string>;
}

const SetupPaymentMethodForm = ({
  athleteEmail,
  athleteFullName,
  athletePhone,
  onSuccess,
  onCancel,
  onError,
  paymentMethodMetadata = {},
}: SetupPaymentMethodFormProps): JSX.Element => {
  const stripe = useStripe();
  const elements = useElements();

  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setLoadingSubmit(true);
    setErrorMessage(undefined);

    const { error: submitError } = await elements.submit();

    if (submitError) {
      setLoadingSubmit(false);
      setErrorMessage(submitError.message);

      if (onError) {
        onError(submitError?.message || 'Failed to submit payment method');
      }

      return;
    }

    const { error, confirmationToken } = await stripe.createConfirmationToken({
      elements,
      params: {
        payment_method_data: {
          metadata: paymentMethodMetadata,
        },
      },
    });

    if (error) {
      setLoadingSubmit(false);

      const errorMsg = error.message || 'Failed to create confirmation token';
      setErrorMessage(errorMsg);

      if (onError) {
        onError(errorMsg);
      }

      return;
    }

    if (!confirmationToken) {
      setLoadingSubmit(false);

      const errorMsg = 'Failed to create confirmation token';
      setErrorMessage(errorMsg);

      if (onError) {
        onError(errorMsg);
      }

      return;
    }

    setLoadingSubmit(false);
    onSuccess(confirmationToken.id);
  };

  return (
    <form onSubmit={handleSubmit}>
      <Stack>
        <Box px='xl'>
          <PaymentElement
            options={{
              defaultValues: {
                billingDetails: {
                  email: athleteEmail,
                  name: athleteFullName,
                  phone: athletePhone || undefined,
                },
              },
            }}
          />
        </Box>
        {errorMessage && (
          <Box px='xl'>
            <Alert color='red'>{errorMessage}</Alert>
          </Box>
        )}
        <Group position='right' mt='xl'>
          <Button variant='outline' disabled={loadingSubmit} type='button' onClick={onCancel}>
            Cancel
          </Button>
          <Button type='submit' disabled={!stripe || !elements || loadingSubmit} loading={loadingSubmit}>
            Save
          </Button>
        </Group>
      </Stack>
    </form>
  );
};

export default SetupPaymentMethodForm;
